import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import { STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import LoadingView from '../LoadingView';
import type { CashflowDetail, UseCashflowResult } from '../../hooks/useCashflow';
import type { CashflowCellContext } from './CashflowTable';
import { SOURCE_STYLE } from './CashflowTable';

type Props = {
  context: CashflowCellContext | null;
  fetchDetail: UseCashflowResult['fetchDetail'];
  onClose: () => void;
};

const fmtYen = (v: number): string => (v < 0 ? `△${Math.abs(v).toLocaleString()}` : v.toLocaleString());
const cellSx = { fontSize: '0.75rem', py: 0.5, whiteSpace: 'nowrap' } as const;
const headSx = { ...cellSx, color: 'grey.600', fontWeight: 600, backgroundColor: '#f8fafc' } as const;

/**
 * 資金繰り表セルの明細ダイアログ。
 * 買掛金支払の予定 (計上実データ) は取引先別、それ以外は現預金に触れる仕訳行を表示する。
 */
export default function CashflowDetailDialog({ context, fetchDetail, onClose }: Props) {
  const [detail, setDetail] = useState<CashflowDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!context) { setDetail(null); setError(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDetail(context.rowKey, context.ym, context.source)
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(e => { if (!cancelled) setError(e?.message || '明細の取得に失敗しました'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [context, fetchDetail]);

  const total = detail
    ? detail.kind === 'payable'
      ? detail.items.reduce((s, i) => s + i.gross_amount, 0)
      : detail.items.reduce((s, i) => s + (i.direction === 'in' ? i.amount : -i.amount), 0)
    : 0;

  return (
    <Dialog open={!!context} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e5e7eb' }}>
        <Icons.ReceiptLong sx={{ fontSize: 18, color: '#64748b' }} />
        {context?.rowLabel} <span style={{ color: '#64748b', fontWeight: 500 }}>{context?.monthLabel}</span>
        {context && (
          <Chip size="small" label={SOURCE_STYLE[context.source].label} sx={{ ml: 1, height: 20, fontSize: '0.7rem', color: SOURCE_STYLE[context.source].color, bgcolor: '#f1f5f9' }} />
        )}
        <Box sx={{ flex: 1 }} />
        {detail && (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
            {detail.items.length} 件 / 合計 {fmtYen(total)} 円
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0, minHeight: 240 }}>
        {loading && <LoadingView message="明細を取得中..." height="240px" size={40} />}
        {error && <Typography color="error" sx={{ p: 2, fontSize: '0.8rem' }}>{error}</Typography>}
        {!loading && detail && detail.kind === 'payable' && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>元システム</TableCell>
                <TableCell sx={headSx}>取引先</TableCell>
                <TableCell sx={headSx} align="right">件数</TableCell>
                <TableCell sx={headSx} align="right">税込支払予定額</TableCell>
                <TableCell sx={headSx} align="right">税抜</TableCell>
                <TableCell sx={headSx}>支払予定日</TableCell>
                <TableCell sx={headSx}>支払条件</TableCell>
                <TableCell sx={headSx}>案件番号</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.items.map(i => (
                <TableRow key={`${i.source}-${i.supplier_code}`} hover>
                  <TableCell sx={cellSx}>{i.source === 'koji' ? '工事管理' : 'Hozenall NET'}</TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'normal' }}>{i.supplier_name || '(名称なし)'} <span style={{ color: '#94a3b8' }}>{i.supplier_code}</span></TableCell>
                  <TableCell sx={cellSx} align="right">{i.count}</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">{fmtYen(i.gross_amount)}</TableCell>
                  <TableCell sx={cellSx} align="right">{fmtYen(i.amount)}</TableCell>
                  <TableCell sx={cellSx}>{i.first_payment_date === i.last_payment_date ? i.first_payment_date : `${i.first_payment_date} 〜 ${i.last_payment_date}`}</TableCell>
                  <TableCell sx={cellSx}>{i.payment_method || '(既定 月末締め翌月末払い)'}{i.terms_source === 'default' && <Chip size="small" label="既定" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />}</TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'normal', maxWidth: 260, color: '#64748b' }}>{i.anken_nos}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && detail && detail.kind === 'journal' && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>日付</TableCell>
                <TableCell sx={headSx}>伝票No</TableCell>
                <TableCell sx={headSx}>入出金</TableCell>
                <TableCell sx={headSx}>相手科目</TableCell>
                <TableCell sx={headSx} align="right">金額</TableCell>
                <TableCell sx={headSx}>取引先</TableCell>
                <TableCell sx={headSx}>部門</TableCell>
                <TableCell sx={headSx}>摘要</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.items.map((i, idx) => (
                <TableRow key={`${i.slip_no}-${idx}`} hover>
                  <TableCell sx={cellSx}>{i.slip_date}</TableCell>
                  <TableCell sx={cellSx}>{i.slip_no}</TableCell>
                  <TableCell sx={{ ...cellSx, color: i.direction === 'in' ? '#047857' : '#b91c1c' }}>{i.direction === 'in' ? '入金' : '出金'}</TableCell>
                  <TableCell sx={cellSx}>{i.counter_account_name} <span style={{ color: '#94a3b8' }}>{i.counter_account_code}</span></TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">{fmtYen(i.amount)}</TableCell>
                  <TableCell sx={cellSx}>{i.supplier_name || ''}</TableCell>
                  <TableCell sx={cellSx}>{i.section_name || ''}</TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'normal', minWidth: 240, color: '#475569' }}>{i.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && detail && detail.items.length === 0 && (
          <Typography sx={{ p: 3, fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>該当する明細がありません</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e5e7eb', py: 0.75 }}>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
}
